import { ArrowBack, Edit as EditIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Container,
    Grid2 as Grid,
    IconButton,
    LinearProgress,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import ApiClient from '../config/ApiClient';
import { AthletePaymentAssignment, CoachContent, PurposeNames, RelEntity } from '../object-actions/types/types';
import { assignmentHasPurposeEntitlement } from '../subscriptions/purposeEntitlements';
import CoachContentReadOnlyView from './CoachContentReadOnlyView';

const PreAssessmentReview: React.FC = () => {
    const { assignment_id, athlete_id } = useParams<{ assignment_id?: string; athlete_id?: string }>();
    const location = useLocation();
    const theme = useTheme();

    const [assignment, setAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coachContents, setCoachContents] = useState<{
        feedback_report: CoachContent | null;
        talking_points: CoachContent | null;
    }>({
        feedback_report: null,
        talking_points: null,
    });
    const [contentLoading, setContentLoading] = useState(false);

    // Check if assignment data or CoachContent IDs were passed via navigation state
    const assignmentFromState = location.state?.assignment as AthletePaymentAssignment | undefined;

    const fetchAssignment = useCallback(async () => {
        if (!assignment_id && !athlete_id) {
            setError('Assignment ID is required');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = assignment_id
                ? `/api/athlete-assignments-by-id/${assignment_id}`
                : `/api/athlete-assignments/${athlete_id}`;
            const response = await ApiClient.get<AthletePaymentAssignment>(url);

            if (response.error) {
                setError(response.error);
            } else if (response.success && response.data) {
                setAssignment(response.data);
            } else {
                setError('No assignment found');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch assignment data');
        } finally {
            setLoading(false);
        }
    }, [assignment_id, athlete_id]);

    // Fetch CoachContent from API
    const fetchCoachContent = useCallback(async (contentId: number): Promise<CoachContent | null> => {
        try {
            const response = await ApiClient.get<CoachContent>(`/api/coach-content/${contentId}/`);
            if (response.error) {
                console.error(`Error fetching coach content ${contentId}:`, response.error);
                return null;
            }
            return response.success && response.data ? response.data : null;
        } catch (err) {
            console.error(`Error fetching coach content ${contentId}:`, err);
            return null;
        }
    }, []);

    // Load coach content from assignment.content_progress (or coach_progress)
    useEffect(() => {
        if (!assignment) return;

        const loadCoachContents = async () => {
            setContentLoading(true);
            const progress = (assignment as any).coach_progress || assignment.content_progress;

            if (!progress) {
                setContentLoading(false);
                return;
            }

            const purposes: Array<'feedback_report' | 'talking_points'> = [
                'feedback_report',
                'talking_points',
            ];

            const contentPromises = purposes.map(async (purpose) => {
                const contentArray = progress[purpose];
                if (contentArray && Array.isArray(contentArray) && contentArray.length > 0) {
                    // Apply filtering logic: if not coach, only show delivered content
                    let filteredContent = contentArray;

                    if (!assignment.my_roles.includes('coach')) {
                        const now = new Date();
                        filteredContent = contentArray.filter((item: RelEntity<'CoachContent'>) => {
                            const coachDelivered = item.entity?.coach_delivered;
                            return coachDelivered && new Date(coachDelivered) < now;
                        });
                    }

                    if (filteredContent && filteredContent.length > 0) {
                        // Get the first (latest) item's ID
                        const contentId = filteredContent[0].id;
                        if (contentId) {
                            const fullContent = await fetchCoachContent(Number(contentId));
                            return { purpose, content: fullContent };
                        }
                    }
                }
                return { purpose, content: null };
            });

            const results = await Promise.all(contentPromises);
            const newContents: {
                feedback_report: CoachContent | null;
                talking_points: CoachContent | null;
            } = {
                feedback_report: null,
                talking_points: null,
            };

            results.forEach(({ purpose, content }) => {
                if (purpose === 'feedback_report') {
                    newContents.feedback_report = content;
                } else if (purpose === 'talking_points') {
                    newContents.talking_points = content;
                }
            });

            setCoachContents(newContents);
            setContentLoading(false);
        };

        loadCoachContents();
    }, [assignment, fetchCoachContent]);

    useEffect(() => {
        if (assignmentFromState) {
            const matchById = assignment_id && assignmentFromState.id === parseInt(assignment_id, 10);
            const matchByAthlete = athlete_id && assignmentFromState.athlete_id === parseInt(athlete_id, 10);
            if (matchById || matchByAthlete) {
                setAssignment(assignmentFromState);
                setLoading(false);
                return;
            }
        }
        fetchAssignment();
    }, [assignment_id, athlete_id, assignmentFromState, fetchAssignment]);

    // Render an Accordion for a document
    const renderDocumentAccordion = (
        title: string,
        content: CoachContent | null,
        purpose: 'feedback_report' | 'talking_points'
    ) => {
        const hasContent = content && (content.body || content.json_body);

        return (
            <Accordion
                defaultExpanded={hasContent ? true : false}
                elevation={2}
                sx={{
                    mb: 2,
                    '&:before': {
                        display: 'none',
                    },
                    boxShadow: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(25, 118, 210, 0.05)',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(25, 118, 210, 0.15)'
                                : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-expanded': {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        minHeight: 64,
                        '& .MuiAccordionSummary-content': {
                            my: 2
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        {hasContent && content && assignment?.my_roles?.includes('coach')
                            && assignmentHasPurposeEntitlement(assignment, purpose) && (
                            <Button
                                component={Link}
                                to={`/coach-content/${content.id}/?edit=true`}
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ textTransform: 'none' }}
                            >
                                Edit
                            </Button>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {contentLoading ? (
                        <LinearProgress />
                    ) : hasContent && content ? (
                        <CoachContentReadOnlyView entity={content} layout="compact" />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {purpose === 'feedback_report' && 'Review detailed feedback and recommendations based on your assessment results.'}
                            {purpose === 'talking_points' && 'Key discussion points and conversation starters for meaningful family discussions.'}
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    if (!assignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No assignment found.
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    component={Link}
                    to={assignment_id ? `/program-progress/${assignment_id}` : `/program-progress/${assignment.id}`}
                    color="secondary"
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Pre-Assessment Review
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={3}>
                    {/* Left Column - Feedback Report */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderDocumentAccordion(
                            PurposeNames.feedback_report,
                            coachContents.feedback_report,
                            'feedback_report'
                        )}
                    </Grid>

                    {/* Right Column - Talking Points and Scheduling Email */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderDocumentAccordion(
                            PurposeNames.talking_points,
                            coachContents.talking_points,
                            'talking_points'
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default PreAssessmentReview;

